import React, { useContext, useEffect, useMemo, useState } from "react"
import { getPlayers } from "../utils/firestore.utils"
import { schoolContext } from "./school.provider"
import { getPlayerFullName, isArchivedPlayer, normalizeName } from "../utils/func.utils"

export const playersContext = React.createContext()

const PlayersProvider = ({ children }) => {
	const { selectedSchool } = useContext(schoolContext)

	const [players, setPlayers] = useState([])
	const [searchedPlayers, setSearchedPlayers] = useState("")
	// gli allievi archiviati restano consultabili, ma solo se li si cerca
	const [showArchived, setShowArchived] = useState(false)
	// Parte da "sto caricando": finché non si è guardato, non si sa se gli
	// allievi non ci sono o non sono ancora arrivati — e le due cose portano
	// a schermate opposte.
	const [isLoading, setLoadingState] = useState(true)
	// Per quale circolo sono gli allievi che abbiamo in mano. Non basta
	// sapere se stiamo caricando: cambiando circolo, per un istante la lista
	// vecchia sembrerebbe la risposta a una domanda nuova.
	const [loadedSchool, setLoadedSchool] = useState(null)

	// allievi attivi: sono quelli che si possono mettere in una lezione
	const activePlayers = useMemo(
		() => (players || []).filter(player => !isArchivedPlayer(player)),
		[players],
	)

	const archivedPlayers = useMemo(
		() => (players || []).filter(isArchivedPlayer),
		[players],
	)

	// la ricerca cerca ogni parola nel nome completo, così "ros mar" trova
	// "Marco Rossi"
	const displayedPlayers = useMemo(() => {
		const listToFilter = showArchived ? archivedPlayers : activePlayers
		const searchedWords = normalizeName(searchedPlayers).split(" ").filter(Boolean)

		if (searchedWords.length === 0) return listToFilter

		return listToFilter.filter(player => {
			// si cerca anche per soprannome: spesso è l'unico nome con cui il
			// maestro conosce l'allievo
			const searchable = normalizeName(
				`${getPlayerFullName(player)} ${player?.nickname ?? ""}`,
			)
			return searchedWords.every(word => searchable.includes(word))
		})
	}, [activePlayers, archivedPlayers, showArchived, searchedPlayers])

	const getPlayerByID = playerID => (players || []).find(player => player.playerID === playerID)

	const handleSearchedPlayerChange = e => {
		setSearchedPlayers(e.target.value)
	}

	const toggleShowArchived = () => setShowArchived(prev => !prev)

	useEffect(() => {
		if (selectedSchool) {
			setLoadingState(true)
			setPlayers([]) // reset immediato

			const unsubscribe = getPlayers(selectedSchool, players => {
				setPlayers(players)
				setLoadedSchool(selectedSchool)
				setLoadingState(false)
			})

			return () => unsubscribe()
		} else {
			// se non c'è scuola selezionata, svuoto comunque
			setPlayers([])
			setLoadedSchool(null)
			setLoadingState(false)
		}
	}, [selectedSchool])

	return (
		<playersContext.Provider
			value={{
				players: displayedPlayers,
				activePlayers,
				archivedPlayers,
				searchedPlayers,
				showArchived,
				isLoading,
				loadedSchool,
				setPlayers,
				setSearchedPlayers,
				handleSearchedPlayerChange,
				toggleShowArchived,
				getPlayerByID,
			}}>
			{children}
		</playersContext.Provider>
	)
}

export default PlayersProvider
